import {
	AufnahmePunktDocument,
	Baumart,
	BodenVegetation,
	bodenformString,
	punktartBeschreibung,
	schichtLabel,
	stfgString,
	wasserformString,
} from '../../data-model';

/**
 * Reine Aufbereitungs-Helfer fuer den formular-getreuen Grube-PDF-Export
 * (SEA-95 „Weisergrubenaufnahme", Punktarten 2 + 4). Plain functions ohne
 * jsPDF/DI, damit das Feld-Mapping und die Listen-Gruppierung ohne Rendering
 * (vitest) testbar sind. Das eigentliche Zeichnen liegt in
 * `ProtokollPdfService.renderGrubeForm`.
 */

// --------------------------------------------------------------------------
// Wert-Formatierer
// --------------------------------------------------------------------------

/** Deutsche Zahldarstellung (Komma als Dezimaltrenner). '' bei undefined/null/NaN; 0 wird angezeigt. */
export function de(n: number | undefined | null): string {
	if (n === undefined || n === null || (typeof n === 'number' && isNaN(n))) return '';
	return String(n).replace('.', ',');
}

/** Wie de(), aber 0/negativ -> '' (fuer Bestandskennwerte: Alter, Hoehe, Bonitaet, Fdw-Haeufung …). */
export function dePos(n: number | undefined | null): string {
	if (n === undefined || n === null || isNaN(n as number) || (n as number) <= 0) return '';
	return de(n);
}

/** Wertepaar "a / b"; leere Haelften entfallen, beide leer -> ''. */
export function pairStr(p: { first?: number; second?: number } | undefined): string {
	if (!p) return '';
	const a = dePos(p.first);
	const b = dePos(p.second);
	if (a && b) return `${a} / ${b}`;
	return a || b || '';
}

/** AltNeu-Wert als "neu (alt: x)" — beide nur wenn vorhanden/abweichend. */
export function altNeuStr(a: { alt?: string; neu?: string } | undefined): string {
	if (!a) return '';
	const neu = (a.neu || '').trim();
	const alt = (a.alt || '').trim();
	if (neu && alt && neu !== alt) return `${neu} (alt: ${alt})`;
	return neu || alt;
}

/** ISO-Datum -> dd.mm.yyyy. Unparsbares bleibt unveraendert; leeres -> ''. */
export function formatDatum(iso: string | undefined): string {
	if (!iso) return '';
	const d = new Date(iso);
	if (isNaN(d.getTime())) return iso;
	const p = (x: number) => String(x).padStart(2, '0');
	return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}

const GEO_SYSTEMS = new Set(['EPSG:4326', 'EPSG:4258']);

/** Koordinatenwert je nach Projektion: geografisch 6 Nachkommastellen, sonst ganzzahlig. */
export function formatKoordinate(v: number | undefined, system: string | undefined): string {
	if (v === undefined || v === null || isNaN(v)) return '';
	return GEO_SYSTEMS.has(system || '') ? v.toFixed(6) : String(Math.round(v));
}

// --------------------------------------------------------------------------
// Bestand (Baumarten-Tabelle)
// --------------------------------------------------------------------------

export interface BaumartRow {
	art: string;
	alter: string;
	hoehe: string;
	durchmesser: string;
	bonitaet: string;
	qualitaet: string;
}

/**
 * Baumarten als Tabellenzeilen. Leeres Array, wenn `baumarten` ein String
 * (Alt-Format/Freitext) oder leer ist. Vollstaendig leere Zeilen werden
 * herausgefiltert. 0-Werte gelten bei Bestandskennwerten als „nicht gesetzt".
 */
export function baumartenRows(baumarten: Baumart[] | string | undefined): BaumartRow[] {
	if (!Array.isArray(baumarten)) return [];
	return baumarten
		.map(b => ({
			art: (b?.art || '').trim(),
			alter: dePos(b?.alter),
			hoehe: pairStr(b?.hoehe),
			durchmesser: pairStr(b?.durchmesser),
			bonitaet: dePos(b?.Bonitaet),
			qualitaet: (b?.qualitaet || '').trim(),
		}))
		.filter(r => r.art || r.alter || r.hoehe || r.durchmesser || r.bonitaet || r.qualitaet);
}

// --------------------------------------------------------------------------
// Vegetationsaufnahme (spaltenweise nach Schicht)
// --------------------------------------------------------------------------

type Schicht = 'b1' | 'b2' | 'str' | 'kr1' | 'kr2' | 'm';
const SCHICHT_ORDER: Schicht[] = ['b1', 'b2', 'str', 'kr1', 'kr2', 'm'];
const SCHICHT_KURZ: Record<string, string> = { b1: 'B1', b2: 'B2', str: 'Str', kr1: 'Kr1', kr2: 'Kr2', m: 'M' };

/** Kurzlabel einer Schicht (B1/Str/Kr1/M …); '' fuer unbekannte/leere Schicht. */
export function schichtKurz(s: string | undefined): string {
	return (s && SCHICHT_KURZ[s]) || '';
}

export interface VegItem {
	name: string;
	deckung: string;
}

export interface VegGruppe {
	schicht: string;
	kurz: string;
	label: string;
	items: VegItem[];
}

function deckungNum(h: string | number | undefined | null): number {
	if (h === undefined || h === null || h === '') return -Infinity;
	const n = typeof h === 'number' ? h : parseFloat(String(h));
	return isNaN(n) ? -Infinity : n;
}

/**
 * Vegetationszeilen nach Schicht gruppiert (Reihenfolge b1,b2,str,kr1,kr2,m,
 * Rest), je Gruppe nach Deckung absteigend sortiert. Zeilen ohne Artnamen
 * werden ignoriert; leere Gruppen entfallen.
 */
export function vegetationGruppen(veg: BodenVegetation | undefined): VegGruppe[] {
	const zeilen = veg?.aufnahme?.vegetationen || [];
	const byS = new Map<string, VegItem[]>();
	for (const z of zeilen) {
		const name = (z?.vegetation || '').trim();
		if (!name) continue;
		const s = z.schicht || '';
		const arr = byS.get(s) || [];
		arr.push({ name, deckung: z.haeufigkeit !== undefined && z.haeufigkeit !== null ? String(z.haeufigkeit) : '' });
		byS.set(s, arr);
	}
	const sortItems = (items: VegItem[]) => items.sort((a, b) => deckungNum(b.deckung) - deckungNum(a.deckung));

	const result: VegGruppe[] = [];
	for (const s of SCHICHT_ORDER) {
		const items = byS.get(s);
		if (items?.length) result.push({ schicht: s, kurz: SCHICHT_KURZ[s], label: schichtLabel(s), items: sortItems(items) });
	}
	const rest = byS.get('');
	if (rest?.length) result.push({ schicht: '', kurz: '', label: 'ohne Schicht', items: sortItems(rest) });
	return result;
}

// --------------------------------------------------------------------------
// Kopfdaten-Raster (Vorderseite)
// --------------------------------------------------------------------------

export interface FormFeld {
	label: string;
	value: string;
	/** Spalten-Gewicht (1 = schmal, 2/3 = breiter); Default 1. */
	w?: number;
}

export interface FormSektion {
	titel: string;
	felder: FormFeld[];
}

/** Vom Service vorberechnete Werte, die hier nicht reproduziert werden (z. B. Reliefstring). */
export interface GrubeKopfDerived {
	relief?: string;
}

/**
 * Zentrales Feld-Mapping der Grube-Vorderseite: Labels + Werte gruppiert in
 * Sektionen. Single Source, damit Beschriftung und Datenherkunft an einer
 * Stelle stehen (und testbar sind). Werte mit klarer Datenquelle werden
 * befuellt; Original-Formularzellen ohne Datenfeld (Land, MTB/AV/TK-Nr.,
 * Geol.-Maßstab/Ausgabe, Standortform) werden bewusst nicht erfunden und im
 * Renderer als leere beschriftete Kaesten gezeichnet.
 */
export function grubeKopfSektionen(bp: AufnahmePunktDocument, derived: GrubeKopfDerived = {}): FormSektion[] {
	const h = bp?.header;
	const eh = bp?.extendedHeader;
	const adr = h?.adresse;
	const koord = h?.koordinaten;
	const forstadresse = [adr?.abteilung, adr?.unterabteilung, adr?.teilflaeche].filter(Boolean).join('');
	const flurFs = [adr?.flur, adr?.flurstueck].filter(v => v !== undefined && v !== null && v !== '').join(' / ');

	// Relief-Detailfelder liegen in extendedHeader.relief.* (Eingabemaske „Naturraum"),
	// nicht in den Top-Level-Feldern (die nur Batch-Edit/Filter nutzen) — daher mit Fallback.
	const neigungGrad = eh?.relief?.neigungGrad ?? eh?.hangneigung;
	const hangneigung = (neigungGrad !== undefined && neigungGrad !== null && !isNaN(neigungGrad))
		? `${de(neigungGrad)}°`
		: (eh?.relief?.neigungsstufe || '');
	const hangrichtung = eh?.relief?.hangrichtung || eh?.hangrichtung || '';

	return [
		{
			titel: 'Identifikation',
			felder: [
				{ label: 'Gruben-Nr.', value: h?.nummer || '' },
				{ label: 'Punktart', value: punktartBeschreibung(bp.type) || bp.type || '' },
				{ label: 'Datum der Aufnahme', value: formatDatum(h?.aufnahme?.datum) },
				{ label: 'Kartierer', value: h?.aufnahme?.kartierer || '' },
				{ label: 'Rechtswert', value: formatKoordinate(koord?.x, koord?.system) },
				{ label: 'Hochwert', value: formatKoordinate(koord?.y, koord?.system) },
				{ label: 'Koord.-System', value: koord?.system || '' },
				{ label: 'Bodenform (i.e.S.)', value: bodenformString(h?.bodenform, 'hbf') || '', w: 2 },
				{ label: 'Labor-Nr.', value: h?.labornummer ? String(h.labornummer) : '' },
			],
		},
		{
			titel: 'Forstadministrative Gliederung',
			felder: [
				{ label: 'Forstamt', value: h?.forstamt || '' },
				{ label: 'Forstbetrieb', value: h?.forstbetrieb || '' },
				{ label: 'Revier', value: h?.revier || '' },
				{ label: 'Abt / Ua / Tf', value: forstadresse },
				{ label: 'Gemarkung', value: adr?.gemarkung || '' },
				{ label: 'Flur / Flurstück', value: flurFs },
				{ label: 'Grd.-Stauwasserform', value: h ? wasserformString(bp) : '' },
			],
		},
		{
			// Untergrund: stratigraphische/morphogenetische Einheit + Ausgangssubstrat (keine Klima-/Relief-Angaben).
			titel: 'Geologie / Substrat',
			felder: [
				{ label: 'Geolog.-stratigr. Einheit', value: [eh?.geologie?.oben, eh?.geologie?.trenner, eh?.geologie?.unten].filter(Boolean).join(' '), w: 2 },
				{ label: 'Morphogen. Einheit', value: (eh?.morphologie || []).filter(Boolean).join(' / ') },
				{ label: 'Grundgest. / Sediment', value: eh?.grundgesteinSediment || '', w: 2 },
				{ label: 'KMgCaP-Serie', value: eh?.kMgCaPSerie || '' },
			],
		},
		{
			// Geländeform und Lage der Grube.
			titel: 'Relief / Lage',
			felder: [
				{ label: 'Reliefform', value: derived.relief || '', w: 2 },
				{ label: 'Mesorelief', value: eh?.mesorelief || '', w: 2 },
				{ label: 'Lage der Grube', value: eh?.lage || '', w: 2 },
				{ label: 'Hangrichtung', value: hangrichtung },
				{ label: 'Hangneigung', value: hangneigung },
				{ label: 'Meereshöhe [m]', value: de(eh?.meereshoehe) },
			],
		},
		{
			// Klimatische Einordnung und forstliche Naturraumgliederung.
			titel: 'Klima / Wuchsgebiet',
			felder: [
				{ label: 'reliefbet. Mesoklima', value: eh?.mesoklima || '' },
				{ label: 'Großklimabereich', value: altNeuStr(eh?.klimabereich) },
				{ label: 'Klimastufe', value: altNeuStr(eh?.klimaStufe) },
				{ label: 'Wuchsgebiet', value: eh?.wuchsgebiet || '', w: 2 },
				{ label: 'Wuchsbezirk', value: eh?.wuchsbezirk || '', w: 2 },
			],
		},
		{
			// Vornutzung und menschliche Eingriffe (Bodenbearbeitung, Düngung, Pestizide, Wasserhaushalt).
			titel: 'Vorbestand / anthropogene Beeinflussung',
			felder: [
				{ label: 'Vorbestandsgeschichte', value: eh?.vorbestandlicheGeschichte || '', w: 3 },
				{ label: 'Forstl. Bodenbearbeitung', value: eh?.forstlicheBodenbearbeitung || '', w: 2 },
				{ label: 'Anthropog. Beeinflussung', value: eh?.anthropogeneBeeinflussung || '', w: 2 },
				{ label: 'Düngemittel-Art', value: eh?.duengemittelArt || '', w: 2 },
				{ label: 'Herbizide', value: eh?.herbizide || '' },
				{ label: 'Insektizide', value: eh?.insektizide || '' },
				{ label: 'Grd.-Stw.-Veränderungen', value: eh?.grundStauwasserVeraenderungen || '', w: 2 },
			],
		},
		{
			// Humusformen (ökologisch je Bezugsfläche + morphologisch) — eigenständige bodenkundliche Kategorie.
			titel: 'Humusform',
			felder: [
				{ label: 'ökol. Humusform (Aufnahmefl.)', value: altNeuStr(h?.humusform?.aufnahmeFlaeche), w: 2 },
				{ label: 'ökol. Humusform (Gesamtfl.)', value: altNeuStr(h?.humusform?.gesamtFlaeche), w: 2 },
				{ label: 'morpholog. Humusform', value: altNeuStr(h?.humusform?.morphologisch), w: 2 },
			],
		},
		{
			// Abgeleitete Standortklassifikation (NICHT der reale Bestand).
			titel: 'Standortformengruppe',
			felder: [
				{ label: 'Standortformengruppe', value: eh?.stfg ? stfgString(bp) : '', w: 2 },
				{ label: 'Nährkraftstufe', value: eh?.stfg?.naehrkraftStufe || '' },
				{ label: 'Substratgruppe', value: eh?.stfg?.substratGruppe || '' },
				{ label: 'Zustand', value: altNeuStr(eh?.stfg?.zustands) },
				{ label: 'Immission', value: altNeuStr(eh?.stfg?.imissions) },
			],
		},
		{
			titel: 'Mosaikbereich / Standortklimatische Angaben',
			felder: [
				{ label: 'Mosaikbereich-Nr.', value: eh?.mosaikBereich?.nummer || '' },
				{ label: 'Mosaikbereich-Name', value: eh?.mosaikBereich?.name || '', w: 2 },
				{ label: 'Mosaik-Haupttyp', value: eh?.mosaikBereich?.haupttyp || '' },
				{ label: 'Standortklimat. Angaben', value: eh?.standortklimatischeAngaben || '', w: 3 },
			],
		},
		{
			// Realer Bestand (Bestockung). Die Baumarten-Tabelle wird im Renderer direkt darunter ausgegeben.
			titel: 'Bestand',
			felder: [
				{ label: 'Bestockungs-Typ', value: altNeuStr(eh?.bestockungsTyp) },
				{ label: 'Begründungsart', value: eh?.begruendungsart || '' },
				{ label: 'Mischform', value: eh?.mischform || '' },
				{ label: 'Waldfunktion', value: eh?.waldfunktion || '' },
			],
		},
	];
}
